/**
 * Force Password Change — full-screen overlay shown when mustChangePassword is true
 */

import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import coreskanLogo from '../assets/coreskan-logo.png';

export function ForcePasswordChange() {
  const { refreshUser } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await api.auth.changePassword(currentPassword, newPassword);
      await refreshUser();
    } catch (err: any) {
      setError(err.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logo}>
          <img src={coreskanLogo} alt="CoreSkan" style={{ height: 40, marginBottom: 8 }} />
        </div>

        <h2 style={styles.heading}>Password Change Required</h2>
        <p style={styles.subtitle}>
          Your administrator has set a temporary password. Please choose a new password to continue.
        </p>

        <form onSubmit={handleSubmit} style={styles.form}>
          {error && <div style={styles.error}>{error}</div>}

          <div style={styles.field}>
            <label style={styles.label}>Current (Temporary) Password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              style={styles.input}
              required
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={styles.input}
              placeholder="At least 8 characters"
              required
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={styles.input}
              required
            />
          </div>

          <button type="submit" style={styles.button} disabled={loading}>
            {loading ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0d0d0d 0%, #1a1a1a 100%)',
  },
  card: {
    background: '#1a1a1a',
    borderRadius: 12,
    padding: 'clamp(20px, 5vw, 40px) clamp(16px, 4vw, 36px)',
    width: 420,
    maxWidth: '90vw',
    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.4)',
    border: '1px solid #333333',
  },
  logo: {
    textAlign: 'center' as const,
    marginBottom: 24,
  },
  heading: {
    color: '#f1f5f9',
    fontSize: 20,
    fontWeight: 600,
    margin: '0 0 8px',
    textAlign: 'center' as const,
  },
  subtitle: {
    color: '#94a3b8',
    fontSize: 13,
    textAlign: 'center' as const,
    margin: '0 0 24px',
    lineHeight: 1.5,
  },
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 16,
  },
  field: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 6,
  },
  label: {
    color: '#94a3b8',
    fontSize: 13,
    fontWeight: 500,
  },
  input: {
    background: '#0d0d0d',
    border: '1px solid #333333',
    borderRadius: 8,
    padding: '10px 14px',
    color: '#f1f5f9',
    fontSize: 16,
    outline: 'none',
    boxSizing: 'border-box' as const,
    width: '100%',
    minHeight: 44,
  },
  button: {
    background: '#4F7CFF',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '12px 0',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 4,
    minHeight: 44,
  },
  error: {
    background: '#7f1d1d',
    color: '#fca5a5',
    padding: '10px 14px',
    borderRadius: 8,
    fontSize: 13,
  },
};
